import { Paths } from "@app/routes";
import { ConfirmAction, WithPermission } from "@atlas/composite";
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardFooter,
  CardTitle,
  cn,
  Label,
  ToolbarGroup,
  useToast,
  WithToolTip,
} from "@atlas/core";
import { useTranslation, useZodSearchParams } from "@atlas/sdk";
import {
  HubPackageStatus,
  IHubCatalogStatus,
  IHubCatalogVisibility,
  type IHubPackageVisibility,
} from "@enums";
import type { IBadgeStripItem, IHubCatalog, IHubPackage } from "@models";
import { BadgeStrip, CatalogIcon, CatalogRating } from "@modules";
import {
  useCreateHubCart,
  useCreateHubPackage,
  useUpdateHubCatalog,
  useUpdateHubPackage,
  useUserInfo,
} from "@query";
import { HUB_PERM } from "@shared";
import { useRemoveFromCart } from "@shared/hooks";
import {
  CatalogVisibilityUpgradeAction,
  getCatalogVisibilityUpgradeAction,
  mergePackageWithCatalogRelation,
  plainCatalogDescription,
  resolveHubCatalogLifecycleBadge,
  resolveHubCatalogTypeBadge,
  resolveHubCatalogVisibilityBadge,
} from "@utils";
import dayjs from "dayjs";
import {
  FileBadge2,
  type LucideIcon,
  PackageCheck,
  Plus,
  Upload,
} from "lucide-react";
import { type MouseEvent, useCallback, useMemo, useState } from "react";
import { z } from "zod";

const searchParamsSchema = z.object({
  tab: z.string().optional(),
});

export interface IHubCatalogOwnerActionItem {
  id: string;
  icon: LucideIcon;
  ariaLabel: string;
  onSelect: () => void;
  variant?: "destructive" | "ghost";
}

interface ICatalogItemCardProps {
  catalog: IHubCatalog;
  onMoreOptions?: (action: string, catalog?: IHubCatalog) => void;
  ownerActionsMenuItems?: IHubCatalogOwnerActionItem[];
  showLifecycleBadge?: boolean;
  showOwnerActionsMenu?: boolean;
  showSubscribeAction?: boolean;
  cartPackage?: IHubPackage;
  isExportSelected?: boolean;
  onExportToggle?: (catalogId: number, version: number) => void;
}

export function CatalogItemCard({
  catalog,
  onMoreOptions,
  ownerActionsMenuItems = [],
  showLifecycleBadge = true,
  showOwnerActionsMenu = true,
  showSubscribeAction = false,
  cartPackage,
  isExportSelected = false,
  onExportToggle,
}: ICatalogItemCardProps) {
  const { t } = useTranslation();
  const { data: userInfo } = useUserInfo();
  const companyId = userInfo?.companyId ?? 0;
  const toast = useToast();
  const { tab } = useZodSearchParams(searchParamsSchema);
  const isExplorer = tab === "explorer";
  const isSubscriptionsTab = tab === "subscriptions";
  const handleCardClick = useCallback(
    () => onMoreOptions?.("details", catalog),
    [catalog, onMoreOptions],
  );

  const lifecycleBadge = useMemo(
    () =>
      showLifecycleBadge
        ? resolveHubCatalogLifecycleBadge(catalog.status, t, catalog.visibility)
        : null,
    [catalog.status, catalog.visibility, t, showLifecycleBadge],
  );

  const subscribeWizardHref = useMemo(
    () =>
      catalog.catalogId != null && catalog.catalogId > 0
        ? `/${Paths.CatalogManager}/${Paths.SubscribeSolution}?${new URLSearchParams({ catalogId: String(catalog.catalogId), version: String(catalog.version), tab: "explorer" }).toString()}`
        : null,
    [catalog.catalogId, catalog.version],
  );

  const showSubscribeButton = useMemo(
    () => showSubscribeAction && subscribeWizardHref != null,
    [showSubscribeAction, subscribeWizardHref],
  );

  const { mutateAsync: createHubPackage, isPending: isCreatingHubPackage } =
    useCreateHubPackage();
  const { mutateAsync: updateHubPackage, isPending: isUpdatingHubPackage } =
    useUpdateHubPackage();
  const { mutateAsync: createHubCart } = useCreateHubCart();
  const { removeFromCart, isRemoving } = useRemoveFromCart();
  const { mutateAsync: updateHubCatalog } = useUpdateHubCatalog();

  const isPackageBusy =
    isCreatingHubPackage || isUpdatingHubPackage || isRemoving;

  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [upgradeConfirmState, setUpgradeConfirmState] = useState<{
    action: CatalogVisibilityUpgradeAction;
  } | null>(null);

  const resolvedCatalogTypeLabel = (catalog.catalogTypeLabel ?? "").trim();

  const isInCart = useMemo(() => {
    const cid = catalog.catalogId ?? 0;
    const version = catalog.version ?? "";
    if (cid <= 0 || cartPackage == null) return false;
    return (cartPackage.catalogRelations ?? []).some(
      (r) => r.catalogId === cid && r.catalogVersion === version,
    );
  }, [cartPackage, catalog.catalogId, catalog.version]);

  const handleRemoveFromCart = useCallback(async () => {
    if (cartPackage == null || !isInCart || isRemoving) return;

    const catalogId = catalog.catalogId ?? 0;
    const version = catalog.version ?? "";

    const removed = await removeFromCart(cartPackage, catalogId, version);
    if (removed) {
      setRemoveConfirmOpen(false);
    }
  }, [
    cartPackage,
    catalog.catalogId,
    catalog.version,
    isInCart,
    isRemoving,
    removeFromCart,
  ]);

  const handleSubscribePlusClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (subscribeWizardHref == null || isInCart) return;

      const catalogId = catalog.catalogId ?? 0;
      const version = catalog.version ?? "";
      if (catalogId <= 0) {
        toast.add({
          title: t("hub.catalog_manager.package_invalid_catalog", {
            defaultValue: "Invalid catalog",
          }),
          data: { type: "error" },
        });
        return;
      }

      if (cartPackage == null) {
        const truncatedName = (catalog.label || catalog.name || "").substring(
          0,
          30,
        );
        const timestamp = dayjs().format("MM/DD/YYYY-HH:mm:ss");
        const generatedLabel = `Pkg-${truncatedName}-${timestamp}`;
        await handleNewPackageCreate({
          label: generatedLabel,
          description: `Auto-generated package for ${catalog.label || catalog.name}`,
          visibility:
            (catalog.visibility as unknown as IHubPackageVisibility) ||
            "Private",
        });
        return;
      }

      const upgradeAction = getCatalogVisibilityUpgradeAction(
        catalog.visibility,
        cartPackage.visibility,
      );

      if (upgradeAction !== CatalogVisibilityUpgradeAction.Allowed) {
        setUpgradeConfirmState({
          action: upgradeAction,
        });
        return;
      }

      if (cartPackage.packageId == null || cartPackage.packageId <= 0) {
        toast.add({
          title: t("hub.catalog_manager.package_update_missing_id", {
            defaultValue: "Could not update package",
          }),
          data: { type: "error" },
        });
        return;
      }

      const alreadyLinked = (cartPackage.catalogRelations ?? []).some(
        (r) => r.catalogId === catalogId && r.catalogVersion === version,
      );
      if (alreadyLinked) {
        toast.add({
          title: t("hub.catalog_manager.package_catalog_already_linked", {
            defaultValue: "This catalog is already linked to your package.",
          }),
          data: { type: "default" },
        });
        return;
      }

      const body = mergePackageWithCatalogRelation(cartPackage, catalog);

      try {
        await updateHubPackage(body);
        toast.add({
          title: t("hub.catalog_manager.package_update_success", {
            defaultValue: "Package updated",
          }),
          data: { type: "success" },
        });
      } catch {
        toast.add({
          title: t("hub.catalog_manager.package_update_error", {
            defaultValue: "Could not update package",
          }),
          data: { type: "error" },
        });
      }
    },
    [
      cartPackage,
      catalog,
      isInCart,
      subscribeWizardHref,
      t,
      toast,
      updateHubPackage,
    ],
  );

  const handleUpgradeConfirm = useCallback(async () => {
    if (!upgradeConfirmState || !cartPackage) return;
    const { action } = upgradeConfirmState;
    setUpgradeConfirmState(null);

    const newVisibility =
      action === CatalogVisibilityUpgradeAction.UpgradeCompany
        ? IHubCatalogVisibility.Company
        : IHubCatalogVisibility.Platform;

    const newStatus =
      action === CatalogVisibilityUpgradeAction.UpgradePlatformReview
        ? IHubCatalogStatus.Review
        : catalog.status;

    try {
      const updatedCatalog = await updateHubCatalog({
        ...catalog,
        visibility: newVisibility,
        status: newStatus,
      });

      if (action === CatalogVisibilityUpgradeAction.UpgradePlatformReview) {
        toast.add({
          title: t("hub.catalog_manager.catalog_review_submitted", {
            defaultValue: "Catalog submitted for review",
          }),
          data: { type: "success" },
        });
      } else {
        const body = mergePackageWithCatalogRelation(
          cartPackage,
          updatedCatalog,
        );
        await updateHubPackage(body);
        toast.add({
          title: t("hub.catalog_manager.package_update_success", {
            defaultValue: "Package updated",
          }),
          data: { type: "success" },
        });
      }
    } catch {
      toast.add({
        title: t("hub.catalog_manager.package_update_error", {
          defaultValue: "Could not update package",
        }),
        data: { type: "error" },
      });
    }
  }, [
    upgradeConfirmState,
    cartPackage,
    catalog,
    updateHubCatalog,
    updateHubPackage,
    t,
    toast,
  ]);

  const handleNewPackageCreate = useCallback(
    async ({
      label,
      description,
      visibility,
    }: {
      label: string;
      description: string;
      visibility: IHubPackageVisibility;
    }) => {
      const body: IHubPackage = {
        companyId,
        name: label,
        label,
        description,
        status: HubPackageStatus.Ready,
        catalogRelations: [
          {
            catalogId: catalog.catalogId ?? 0,
            catalogVersion: catalog.version ?? 0,
          },
        ],
        visibility,
      };

      await createHubPackage(body, {
        onSuccess: async (newPackage: IHubPackage) => {
          if (cartPackage == null && newPackage.packageId != null) {
            try {
              await createHubCart({ packageId: newPackage.packageId });
            } catch (e) {
              console.error("Failed to create hub cart", e);
            }
          }
          toast.add({
            title: t("hub.catalog_manager.package_create_success", {
              defaultValue: "Package added",
            }),
            data: { type: "success" },
          });
        },
        onError: () => {
          toast.add({
            title: t("hub.catalog_manager.package_create_error", {
              defaultValue: "Could not add package",
            }),
            data: { type: "error" },
          });
        },
      });
    },
    [
      catalog.catalogId,
      catalog.version,
      createHubPackage,
      createHubCart,
      cartPackage,
      t,
      toast,
      companyId,
    ],
  );

  const cardBadgeStripItems = useMemo((): IBadgeStripItem[] => {
    const items: IBadgeStripItem[] = [];
    const catalogTypeBadge = resolveHubCatalogTypeBadge(
      resolvedCatalogTypeLabel,
    );
    if (catalogTypeBadge) {
      items.push(catalogTypeBadge);
    }

    if (
      showLifecycleBadge &&
      lifecycleBadge &&
      !isExplorer &&
      !isSubscriptionsTab
    ) {
      items.push(lifecycleBadge);
    }
    return items;
  }, [
    resolvedCatalogTypeLabel,
    showLifecycleBadge,
    lifecycleBadge,
    isExplorer,
    isSubscriptionsTab,
    catalog.visibility,
    t,
  ]);

  return (
    <>
      <Card
        className={cn(
          "h-[263px] w-[345px] cursor-pointer",
          isExportSelected && "border-primary",
        )}
        onClick={handleCardClick}>
        <CardContent className="flex flex-col gap-4 p-4">
          <BadgeStrip className="gap-2.5" items={cardBadgeStripItems} />
          <div className="flex gap-4">
            <CatalogIcon icon={catalog.icon} alt={catalog.label} />
            <div className="flex flex-col gap-2 flex-1">
              <CardTitle>{catalog.label}</CardTitle>
              {catalog.supportedCloud.length > 0 && (
                <div className=" flex flex-wrap items-center gap-1.5">
                  <Badge size="sm" variant="secondary" appearance="light">
                    {catalog.supportedCloud[0]}
                  </Badge>
                  {catalog.supportedCloud.length > 1 && (
                    <Badge size="sm" variant="secondary" appearance="light">
                      +{catalog.supportedCloud.length - 1}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-3 self-stretch">
            {(catalog.catalogId ?? 0) > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <CatalogRating averageRating={catalog.averageRating} />
                {catalog.featured && (
                  <div className="flex items-center gap-1.5">
                    <FileBadge2
                      className={cn("size-4 shrink-0 text-success-foreground ")}
                      aria-hidden
                    />
                    <Label className="text-sm text-muted-foreground font-medium">
                      {t("hub.catalog_manager.featured", {
                        defaultValue: "Featured",
                      })}
                    </Label>
                  </div>
                )}
              </div>
            )}
            <Label className="text-xs text-muted-foreground line-clamp-2">
              {plainCatalogDescription(catalog.description ?? "")}
            </Label>
          </div>
        </CardContent>

        <CardFooter
          onClick={(e) => e.stopPropagation()}
          className="w-full justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge className="shrink-0" size="sm" variant="secondary">
              {t("hub.catalog_manager.card_version", {
                version: String(catalog.version ?? ""),
                defaultValue: "Version {{version}}",
              })}
            </Badge>
            {catalog.visibility &&
              (() => {
                const visibilityBadge = resolveHubCatalogVisibilityBadge(
                  catalog.visibility,
                  t,
                );
                if (!visibilityBadge) return null;
                return (
                  <Badge
                    className="shrink-0"
                    size={visibilityBadge.size}
                    appearance={visibilityBadge.appearance}
                    variant={visibilityBadge.variant}>
                    {visibilityBadge.label}
                  </Badge>
                );
              })()}
          </div>

          <div className="flex shrink-0 items-center justify-end gap-1">
            {onExportToggle && (
              <WithToolTip
                tooltipText={
                  isExportSelected
                    ? t("hub.catalog_manager.export_remove_aria", {
                        defaultValue: "Remove from export",
                      })
                    : t("hub.catalog_manager.export_select_aria", {
                        defaultValue: "Select for export",
                      })
                }>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (catalog.catalogId && catalog.version) {
                      onExportToggle(
                        catalog.catalogId,
                        Number(catalog.version),
                      );
                      if (isExportSelected) {
                        toast.add({
                          title: t(
                            "hub.catalog_manager.export_catalog_removed",
                            {
                              defaultValue: "Catalog removed from export list",
                            },
                          ),
                          data: { type: "default" },
                        });
                      } else {
                        toast.add({
                          title: t("hub.catalog_manager.export_catalog_added", {
                            defaultValue: "Catalog added to export list",
                          }),
                          data: { type: "success" },
                        });
                      }
                    }
                  }}
                  aria-label={t("hub.catalog_manager.export_select_aria", {
                    defaultValue: "Select for export",
                  })}>
                  <Upload
                    className={cn("size-4", isExportSelected && "text-primary")}
                  />
                </Button>
              </WithToolTip>
            )}
            {showSubscribeButton && (
              <WithPermission permissions={[HUB_PERM.CATALOG_BUY]}>
                <WithToolTip
                  tooltipText={
                    isInCart
                      ? t("hub.catalog_manager.remove_from_cart_aria", {
                          defaultValue: "Remove from package",
                        })
                      : t("hub.catalog_manager.add_to_cart_aria", {
                          defaultValue: "Add to package",
                        })
                  }
                  side="top"
                  sideOffset={4}
                  className="max-w-xs">
                  <ToolbarGroup className="inline-flex w-fit shrink-0 gap-0 p-0 shadow-none">
                    <Button
                      type="button"
                      variant="ghost"
                      appearance="ghost"
                      size="icon"
                      className={cn(
                        "size-8",
                        isInCart
                          ? "text-primary bg-primary/15 hover:bg-primary/15 hover:text-primary disabled:opacity-100"
                          : "transition-colors",
                      )}
                      disabled={isPackageBusy}
                      aria-label={
                        isInCart
                          ? t("hub.catalog_manager.remove_from_cart_aria", {
                              defaultValue: "Remove from package",
                            })
                          : t("hub.catalog_manager.add_to_cart_aria", {
                              defaultValue: "Add to package",
                            })
                      }
                      onClick={
                        isInCart
                          ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (isRemoving) return;
                              setRemoveConfirmOpen(true);
                            }
                          : handleSubscribePlusClick
                      }>
                      {isInCart ? (
                        <PackageCheck className="size-4" aria-hidden />
                      ) : (
                        <Plus className="size-4" aria-hidden />
                      )}
                    </Button>
                  </ToolbarGroup>
                </WithToolTip>
              </WithPermission>
            )}
            {showOwnerActionsMenu && ownerActionsMenuItems.length > 0 && (
              <WithPermission permissions={[HUB_PERM.CATALOG_EDIT]}>
                <ButtonGroup className="inline-flex w-fit shrink-0 shadow-none">
                  {ownerActionsMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Button
                        key={item.id}
                        type="button"
                        variant={item.variant}
                        appearance="ghost"
                        size="icon"
                        className="size-8 shrink-0"
                        aria-label={item.ariaLabel}
                        onClick={(e) => {
                          e.stopPropagation();
                          item.onSelect();
                        }}>
                        <Icon className="size-4" aria-hidden />
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </WithPermission>
            )}
          </div>
        </CardFooter>
      </Card>
      <ConfirmAction
        open={removeConfirmOpen}
        onOpenChange={(open) => {
          if (!open && isRemoving) return;
          setRemoveConfirmOpen(open);
        }}
        title={t("hub.checkout.remove_confirm_title", {
          defaultValue: "Remove from package?",
        })}
        description={t("hub.checkout.remove_confirm_description", {
          name: (catalog.label ?? catalog.name ?? "").trim() || "",
          defaultValue:
            'Remove "{{name}}" from your package? You can add it again from the catalog later.',
        })}
        confirmText={t("hub.checkout.remove", {
          defaultValue: "Remove",
        })}
        cancelText={t("hub.administer.cancel", {
          defaultValue: "Cancel",
        })}
        actionVariant="destructive"
        primaryActionLoading={isRemoving}
        onConfirm={handleRemoveFromCart}
      />
      <ConfirmAction
        open={upgradeConfirmState != null}
        onOpenChange={(open) => !open && setUpgradeConfirmState(null)}
        title={
          upgradeConfirmState?.action ===
          CatalogVisibilityUpgradeAction.UpgradePlatformReview
            ? t("hub.checkout.upgrade_platform_confirm_title", {
                defaultValue:
                  "Update catalog visibility and submit for review?",
              })
            : t("hub.checkout.upgrade_confirm_title", {
                defaultValue: "Update catalog visibility?",
              })
        }
        description={
          upgradeConfirmState?.action ===
          CatalogVisibilityUpgradeAction.UpgradeCompany
            ? t("hub.checkout.upgrade_company_description", {
                defaultValue:
                  "To add this catalog to your package, its visibility will be changed to Company. Do you want to proceed?",
              })
            : t("hub.checkout.upgrade_platform_description", {
                defaultValue:
                  "To add this catalog to your package, its visibility will be changed to Platform and it will be submitted for review. Do you want to proceed?",
              })
        }
        confirmText={t("hub.checkout.upgrade", {
          defaultValue: "Update",
        })}
        cancelText={t("hub.administer.cancel", {
          defaultValue: "Cancel",
        })}
        actionVariant="default"
        onConfirm={handleUpgradeConfirm}
      />
    </>
  );
}
